import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { extractApiError } from '../../utils/extractApiError';

// Enablement is practice-specific and must be refreshed from the server.
// Never reuse a global cached value across practices.
(() => {
    try {
        localStorage.removeItem('pmsEnabledGlobal');
    } catch {
        /* noop */
    }
})();

const initialState = {
    isLoading: false,
    pmsEnabledGlobal: false,
    enablementPracticeId: null,
    enablementStatus: 'idle',
    enablementRequest: null,
    scope: null,
    scheduleStatus: 'idle',
    requests: {},
    readStatuses: {},
    errors: {},
    schedules: [],
    slots: [],
    overrides: [],
    error: null,
};

const slice = createSlice({
    name: 'pmsSchedule',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, { ...initialState, pmsEnabledGlobal: false });
            try { localStorage.removeItem('pmsEnabledGlobal'); } catch { /* noop */ }
        },
        startEnablement(state, action) {
            state.enablementPracticeId = action.payload.practiceId;
            state.enablementRequest = action.payload.request;
            state.enablementStatus = 'loading';
            state.pmsEnabledGlobal = false;
        },
        finishEnablement(state, action) {
            if (state.enablementRequest !== action.payload.request) return;
            state.enablementStatus = action.payload.status;
            state.pmsEnabledGlobal = action.payload.enabled === true;
        },
        startRead(state, { payload }) {
            if (state.scope !== payload.scope) {
                state.schedules = [];
                state.slots = [];
                state.scheduleStatus = 'idle';
                state.requests = {};
                state.readStatuses = {};
                state.errors = {};
            }
            state.scope = payload.scope;
            state.requests[payload.kind] = payload.request;
            state.readStatuses[payload.kind] = 'loading';
            state.errors[payload.kind] = null;
            state.isLoading = true;
            state.error = null;
            if (payload.kind === 'schedules') state.scheduleStatus = 'loading';
        },
        finishRead(state, { payload }) {
            if (state.scope !== payload.scope || state.requests[payload.kind] !== payload.request) return;
            state.readStatuses[payload.kind] = payload.error ? 'error' : 'ready';
            state.isLoading = Object.values(state.readStatuses).includes('loading');
            state.error = payload.error || null;
            state.errors[payload.kind] = payload.error || null;
            if (payload.kind === 'schedules') state.scheduleStatus = payload.error ? 'error' : 'ready';
            state[payload.kind] = payload.error ? [] : payload.rows;
        },
        mutationError(state, { payload }) {
            if (state.scope === payload.scope) {
                state.error = payload.error;
                state.errors.schedules = payload.error;
            }
        },
        updatePmsEnabledGlobal(state, action) {
            state.pmsEnabledGlobal = action.payload;
            // Do not persist a global flag across practice changes.
            try { localStorage.removeItem('pmsEnabledGlobal'); } catch { /* noop */ }
        },

    },
});

export default slice.reducer;

export const { updatePmsEnabledGlobal } = slice.actions;

// Check whether PMS scheduling is enabled for a practice.
// Cache only the authoritative preference here. usePmsSchedulingScope combines it
// with a settled permission snapshot for the same practice before booking can render.
let enablementRequest = 0;

export function checkPmsEnabled(practiceId, { force = false } = {}) {
    return async (thunkDispatch, getState) => {
        const target = String(practiceId || '');
        if (!target || String(getState().practiceTreatmentList?.practiceId) !== target) return;
        const cached = getState().pmsSchedule;
        if (!force && cached.enablementPracticeId === target
            && ['loading', 'ready', 'error'].includes(cached.enablementStatus)) return;
        enablementRequest += 1;
        const request = enablementRequest;
        thunkDispatch(slice.actions.startEnablement({ practiceId: target, request }));
        const isCurrent = () => String(getState().practiceTreatmentList?.practiceId) === target
            && getState().pmsSchedule.enablementRequest === request;
        try {
            const response = await callApi(`/api/v1/practices/${target}/`);
            const enabled = response?.data?.preferences?.is_pms_scheduling_enabled;
            if (!response?.ok || typeof enabled !== 'boolean') throw new Error('Practice scheduling configuration unavailable');
            if (isCurrent()) thunkDispatch(slice.actions.finishEnablement({ request, status: 'ready', enabled }));
        } catch {
            if (isCurrent()) thunkDispatch(slice.actions.finishEnablement({ request, status: 'error', enabled: false }));
        } finally {
            // A route can unmount before the new practice starts its own load.
            // Do not leave this completed, superseded request cached as loading.
            if (String(getState().practiceTreatmentList?.practiceId) !== target
                && getState().pmsSchedule.enablementRequest === request) {
                thunkDispatch(slice.actions.finishEnablement({ request, status: 'idle', enabled: false }));
            }
        }
    };
}

let readRequest = 0;
const scheduleBase = (practiceId, practitionerId) => practitionerId === 'self'
    ? `/api/v1/practices/${practiceId}/pms/my-schedules/`
    : `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/`;
const scopeOf = (practiceId, practitionerId) => `${practiceId}:${practitionerId}`;
const messageOf = (response) => extractApiError(response?.data || response?.error?.message, 'Unable to load or save schedule. Please try again.');

function readSchedule(practiceId, practitionerId, kind, suffix = '') {
    return async (thunkDispatch, getState) => {
        const scope = scopeOf(practiceId, practitionerId);
        if (!practiceId || !practitionerId || String(getState().practiceTreatmentList?.practiceId) !== String(practiceId)) return;
        readRequest += 1;
        const request = readRequest;
        thunkDispatch(slice.actions.startRead({ scope, request, kind }));
        const current = () => String(getState().practiceTreatmentList?.practiceId) === String(practiceId)
            && getState().pmsSchedule.scope === scope && getState().pmsSchedule.requests[kind] === request;
        try {
            const response = await callApi(`${scheduleBase(practiceId, practitionerId)}${suffix}`);
            const rows = response?.data?.schedules ?? response?.data?.results ?? response?.data;
            if (!response?.ok || !Array.isArray(rows)) throw new Error(messageOf(response));
            if (current()) thunkDispatch(slice.actions.finishRead({ scope, request, kind, rows }));
        } catch (error) {
            if (current()) thunkDispatch(slice.actions.finishRead({ scope, request, kind, error: error.message || 'Unable to load schedule.' }));
        }
    };
}

export const fetchPractitionerSchedules = (practiceId, practitionerId) => readSchedule(practiceId, practitionerId, 'schedules');
export const fetchSlots = (practiceId, practitionerId, scheduleId, params) => readSchedule(practiceId, practitionerId, 'slots', `${scheduleId}/slots/${params ? `?${new URLSearchParams(params)}` : ''}`);

function mutateSchedule(practiceId, practitionerId, suffix, method, data, onSuccess) {
    return async (thunkDispatch, getState) => {
        const scope = scopeOf(practiceId, practitionerId);
        const request = getState().pmsSchedule.requests.schedules;
        const current = () => String(getState().practiceTreatmentList?.practiceId) === String(practiceId)
            && getState().pmsSchedule.scope === scope && getState().pmsSchedule.requests.schedules === request;
        if (!practiceId || !practitionerId || !current() || getState().pmsSchedule.scheduleStatus !== 'ready') return { ok: false, stale: true };
        const scheduleId = suffix.split('/')[0];
        if (scheduleId && !getState().pmsSchedule.schedules.some((schedule) => String(schedule.id) === scheduleId)) return { ok: false, stale: true };
        try {
            const response = await callApi(`${scheduleBase(practiceId, practitionerId)}${suffix}`, method, data);
            if (!current()) return { ok: false, stale: true };
            if (response?.ok) onSuccess?.(response.data);
            else thunkDispatch(slice.actions.mutationError({ scope, error: messageOf(response) }));
            return response;
        } catch (error) {
            if (!current()) return { ok: false, stale: true };
            thunkDispatch(slice.actions.mutationError({ scope, error: error.message || 'Unable to save schedule.' }));
            return { ok: false };
        }
    };
}
export const createSchedule = (p, u, data, done) => mutateSchedule(p, u, '', 'POST', data, done);
export const updateSchedule = (p, u, id, data, done) => mutateSchedule(p, u, `${id}/`, 'PATCH', data, done);
export const deleteSchedule = (p, u, id, done) => mutateSchedule(p, u, `${id}/`, 'DELETE', null, done);
export const createOverride = (p, u, id, data, done) => mutateSchedule(p, u, `${id}/overrides/`, 'POST', data, done);
export const deleteOverride = (p, u, id, overrideId, done) => mutateSchedule(p, u, `${id}/overrides/${overrideId}/`, 'DELETE', null, done);
